import { purry as rPurry } from 'remeda';

import type { components, OrganizationAccessControlModuleEnum } from '@/libs/openapi';

export type UserWithPermissions<Subject, Action> = {
  permissions: ReadonlyArray<{
    subject: Subject;
    actions: ReadonlyArray<Action>;
  }>;
} | null | undefined;

export type PermissionActions<Action> = ReadonlyArray<Action | ReadonlyArray<Action>>;

const hasPermissionsImpl = <Subject, Action>(
  user: UserWithPermissions<Subject, Action>,
  subject: Subject,
  actions: PermissionActions<Action>,
): boolean => {
  if (!user || user.permissions.length === 0) {
    return false;
  }

  if (actions.length === 0) {
    return true;
  }

  const allowedActions = new Set<Action>();
  for (const permission of user.permissions) {
    if (permission.subject === subject) {
      for (const action of permission.actions) {
        allowedActions.add(action);
      }
    }
  }

  return actions.some((action) => (Array.isArray(action)
    ? action.every((a: Action) => allowedActions.has(a))
    : allowedActions.has(action as Action)));
};

export function hasPermissions<Subject, Action>(user: UserWithPermissions<Subject, Action>, subject: Subject, actions: PermissionActions<Action>): boolean;
export function hasPermissions<Subject, Action>(subject: Subject, actions: PermissionActions<Action>): (user: UserWithPermissions<Subject, Action>) => boolean;
export function hasPermissions(...args: readonly unknown[]): unknown {
  return rPurry(hasPermissionsImpl, args);
}

const hasModuleImpl = (
  organization: components['schemas']['OrganizationResponseDto'] | undefined,
  module: OrganizationAccessControlModuleEnum,
): boolean => organization?.accessControlModules?.includes(module) ?? false;

export function hasModule(organization: components['schemas']['OrganizationResponseDto'] | undefined, module: OrganizationAccessControlModuleEnum): boolean;
export function hasModule(module: OrganizationAccessControlModuleEnum): (organization: components['schemas']['OrganizationResponseDto'] | undefined) => boolean;
export function hasModule(...args: readonly unknown[]): unknown {
  return rPurry(hasModuleImpl, args);
}
